/**
 * @file useConversationController.ts
 * @description Всё об открытом диалоге в одном месте для переписки и панели
 * сведений: данные (`useConversation`), отправка и черновик, действия,
 * открытые окна («Передать», «Вернуть оператору», шторка сведений), переходы
 * «Открыть заказ» / «Карточка клиента» и выбор пункта меню «⋯».
 *
 * «Открыть заказ» из развёрнутого окна сворачивает его до компактного и
 * открывает заказ под окном; до 1024 окно закрывается (§1в, §6).
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import type { ChatActionKey } from "./chat-actions";
import { useChatWindow } from "./chat-window-context";
import { useConversation } from "./useConversation";
import { useConversationActions } from "./useConversationActions";
import type { DraftRestore } from "./MessageComposer";
import { useMessageSender } from "./useMessageSender";
import { useServiceRecordsPref } from "./useServiceRecordsPref";

/** Вкладка браузера активна — условие отметки «прочитано». */
function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState === "visible");
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return visible;
}

export function useConversationController(id: string | null) {
  const chat = useChatWindow();
  const navigate = useNavigate();
  const pageVisible = usePageVisible();
  const onScreen = chat.state.open && pageVisible && id !== null;
  const conv = useConversation(id, onScreen, chat.bumpData);
  // Черновик — в поле ввода; сюда только просьба «вернуть текст в поле».
  const [restoreDraft, setRestoreDraft] = useState<DraftRestore | null>(null);
  const [serviceShown, toggleService] = useServiceRecordsPref(chat.userId);
  const [transferOpen, setTransferOpen] = useState(false);
  const [confirmReturn, setConfirmReturn] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [focusEvent, setFocusEvent] = useState<{ id: string; nonce: number } | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState<string | null>(null);

  useEffect(() => {
    setTransferOpen(false);
    setConfirmReturn(false);
    setInfoOpen(false);
    setFocusEvent(null);
  }, [id]);

  const sender = useMessageSender(id, {
    onSent: (message) => {
      conv.mergeMessages([message]);
      // Полоса «Диалог передан вам» исчезает после ответа (§3).
      setBannerDismissed(conv.transferredToMe?.id ?? null);
      chat.bumpData();
    },
    onStale: () => void conv.refreshDetail(),
    onRestoreDraft: (body) => setRestoreDraft({ body, nonce: Date.now() }),
  });

  const actions = useConversationActions(conv.detail, {
    setDetail: conv.setDetail,
    refreshDetail: conv.refreshDetail,
    onReturned: () => chat.select(null),
    bumpData: chat.bumpData,
  });

  /** Уход на страницу кабинета из окна. */
  const goTo = useCallback(
    (path: string) => {
      if (chat.presentation.frame === "full") {
        if (chat.presentation.canExpand) chat.setExpanded(false);
        // Шаги истории не снимаем: history.go отработал бы после перехода и увёл назад.
        else chat.hide();
      }
      navigate(`/shop/${chat.slug}/${path}`);
    },
    [chat, navigate],
  );

  const detail = conv.detail;
  const openOrder = detail?.contextOrder && chat.canOpenOrders ? () => goTo(`orders/${detail.contextOrder?.id ?? ""}`) : null;
  const openCustomer = detail && chat.canOpenCustomers ? () => goTo(`customers/${detail.customerId}`) : null;

  /** Пункт меню «⋯» → действие (реестр, а не каскад условий). */
  const runMenu = (key: ChatActionKey) => {
    const runners: Record<ChatActionKey, () => void> = {
      take: () => void actions.take(),
      transfer: () => setTransferOpen(true),
      returnToQueue: () => setConfirmReturn(true),
      archive: () => void actions.archive(),
      unarchive: () => void actions.unarchive(),
      openOrder: () => openOrder?.(),
      openCustomer: () => openCustomer?.(),
      toggleService,
    };
    runners[key]();
  };

  // Передача мне — запись, с которой диалог открыт (признак на сервере гаснет
  // отметкой «прочитано», полоса держится до «Понятно» или ответа).
  const transferredEvent = conv.transferredToMe;
  const transferredEventId = transferredEvent?.id ?? null;

  return {
    chat,
    conv,
    restoreDraft,
    sender,
    actions,
    serviceShown,
    toggleService,
    transferOpen,
    setTransferOpen,
    confirmReturn,
    setConfirmReturn,
    infoOpen,
    setInfoOpen,
    focusEvent,
    focusOn: (eventId: string) => setFocusEvent({ id: eventId, nonce: Date.now() }),
    openOrder,
    openCustomer,
    runMenu,
    transferredEventId,
    transferredEvent: transferredEventId !== null && bannerDismissed !== transferredEventId ? transferredEvent : null,
    dismissBanner: () => setBannerDismissed(transferredEventId),
  };
}

export type ConversationController = ReturnType<typeof useConversationController>;
